import { useMemo, useState } from "react";
import { AlertTriangle, Calculator, X, ChevronRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/lib/numberFormatting";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import portelinLogo from "@/assets/portelin.jpg";
import type { ProcessedFile } from "@/components/InvoiceList";

/**
 * Asistente Virtual — Validador Proactivo IA.
 * Analiza las facturas procesadas y avisa de descuadres entre
 * el total declarado y el calculado (base + IVA − IRPF).
 */

type Severity = "high" | "medium" | "low";
type FindingType = "total_mismatch" | "missing_total" | "credit_note";

interface Finding {
  id: string;
  fileId: string;
  type: FindingType;
  severity: Severity;
  title: string;
  invoice: string;
  detail: string;
  suggestion: string;
}

interface AIValidatorDemoProps {
  processed: ProcessedFile[];
  onOpenInvoice: (fileId: string) => void;
}

const fmtEUR = (n: number | undefined) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    : "—";

const sumBase = (f: ProcessedFile): number =>
  (f.vatBreakdown ?? []).reduce((acc, l) => acc + (Number(l.taxableBase) || 0), 0);

const sumVat = (f: ProcessedFile): number =>
  (f.vatBreakdown ?? []).reduce((acc, l) => acc + (Number(l.vatAmount) || 0), 0);

const computeFindings = (processed: ProcessedFile[]): Finding[] => {
  const findings: Finding[] = [];

  // Descuadres total vs calculado
  processed.forEach((f) => {
    const totalValue = Number(f.totalAmount);
    const hasTotal = Number.isFinite(totalValue) && totalValue !== 0;
    if (!hasTotal) {
      const base = sumBase(f);
      const vat = sumVat(f);
      const irpf = Number(f.irpfAmount) || 0;
      const calc = base + vat - irpf;
      findings.push({
        id: `nt-${f.id}`,
        fileId: f.id,
        type: "missing_total",
        severity: "high",
        title: "Total de la factura sin detectar",
        invoice: `${f.supplier || "—"} · ${f.invoiceNumber || "s/nº"}`,
        detail:
          calc !== 0
            ? `No se ha extraído el total de la factura. Según las líneas, base ${fmtEUR(base)} + IVA ${fmtEUR(vat)}${irpf ? ` − IRPF ${fmtEUR(irpf)}` : ""} = ${fmtEUR(calc)}.`
            : "No se ha extraído el total de la factura ni hay líneas de base/IVA con importe.",
        suggestion:
          calc !== 0
            ? `Revisa el documento y escribe el total (probablemente ${fmtEUR(calc)}) antes de exportar.`
            : "Abre la factura y completa los importes manualmente antes de exportar.",
      });
      return;
    }
    const base = sumBase(f);
    const vat = sumVat(f);
    const irpf = Number(f.irpfAmount) || 0;
    const calc = base + vat - irpf;
    const diff = Number((f.totalAmount - calc).toFixed(2));
    if (Math.abs(diff) >= 0.01) {
      const abs = Math.abs(diff);
      const sev: Severity = abs > 1 ? "high" : abs > 0.05 ? "medium" : "low";
      findings.push({
        id: `mm-${f.id}`,
        fileId: f.id,
        type: "total_mismatch",
        severity: sev,
        title: "Total no cuadra con base + IVA",
        invoice: `${f.supplier || "—"} · ${f.invoiceNumber || "s/nº"} · ${fmtEUR(f.totalAmount)}`,
        detail: `Base ${fmtEUR(base)} + IVA ${fmtEUR(vat)}${irpf ? ` − IRPF ${fmtEUR(irpf)}` : ""} = ${fmtEUR(calc)}. La factura indica ${fmtEUR(f.totalAmount)} (diferencia ${diff > 0 ? "+" : ""}${formatNumber(diff)} €).`,
        suggestion:
          abs > 1
            ? "Revisa si falta una línea de IVA, un recargo o un IRPF no extraído."
            : abs > 0.05
              ? "Diferencia significativa — revisa importes antes de exportar."
              : "Diferencia mínima (céntimo de redondeo) — ajusta para que SUENLACE acepte la factura.",
      });
    }
  });

  // Abonos / rectificativas mal detectados
  processed.forEach((f) => {
    const label = `${f.supplier || "—"} · ${f.invoiceNumber || "s/nº"} · ${fmtEUR(f.totalAmount)}`;
    const hints = f.creditNoteHints ?? [];
    const level = f.creditNoteHintLevel ?? "none";
    if (f.creditNoteReviewed) {
      // El usuario ya decidió el signo manualmente.
    } else if (!f.isCreditNote && level !== "none") {
      findings.push({
        id: `cn-${f.id}`,
        fileId: f.id,
        type: "credit_note",
        severity: level === "strong" ? "high" : "medium",
        title: "Posible factura de abono no marcada",
        invoice: label,
        detail: `En el documento aparece: ${hints.join(", ")}. La factura se ha guardado con importes positivos, como una factura corriente.`,
        suggestion:
          "Abre la factura y, si es un abono, introduce los importes en negativo; si no lo es, puedes ignorar este aviso.",
      });
    } else if (f.isCreditNote && level === "none") {
      findings.push({
        id: `cn-${f.id}`,
        fileId: f.id,
        type: "credit_note",
        severity: "medium",
        title: "Marcada como abono sin indicios en el documento",
        invoice: label,
        detail:
          "La factura está marcada como abono/rectificativa, pero en el texto del documento no aparece ninguna referencia a abono ni importes negativos.",
        suggestion: "Confirma que es realmente un abono; si no lo es, pon los importes en positivo.",
      });
    }
  });

  // Orden: high → medium → low
  const rank: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
  return findings.sort((a, b) => rank[a.severity] - rank[b.severity]);
};

const severityStyles: Record<Severity, string> = {
  high: "border-destructive/40 bg-destructive/5",
  medium: "border-amber-300/50 bg-amber-50 dark:bg-amber-950/20",
  low: "border-muted bg-muted/30",
};

const severityBadge: Record<Severity, { label: string; variant: "destructive" | "secondary" | "outline" }> = {
  high: { label: "Alta", variant: "destructive" },
  medium: { label: "Media", variant: "secondary" },
  low: { label: "Baja", variant: "outline" },
};

const typeIcon = (_t: FindingType) => <Calculator className="h-4 w-4" />;

const AIValidatorDemo = ({ processed, onOpenInvoice }: AIValidatorDemoProps) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const findings = useMemo(() => computeFindings(processed), [processed]);
  const visible = findings.filter((f) => !dismissed.has(f.id));
  const highCount = visible.filter((f) => f.severity === "high").length;

  // Si no hay facturas procesadas, ocultamos el botón para no saturar.
  if (processed.length === 0) return null;

  const handleGoTo = (fileId: string) => {
    setOpen(false);
    // pequeño retardo para que el Sheet termine de cerrar antes de abrir el split view
    setTimeout(() => onOpenInvoice(fileId), 80);
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[hsl(150_65%_38%)] py-2 pl-2 pr-4 text-white shadow-lg ring-1 ring-black/10 transition-transform active:scale-95"
        aria-label="Abrir Asistente Virtual"
      >
        <span className="relative">
          <img
            src={portelinLogo}
            alt="Asistente Virtual"
            className="h-9 w-9 rounded-full object-cover ring-2 ring-white/40"
          />
          {visible.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-400 ring-2 ring-[hsl(150_65%_38%)]" />
            </span>
          )}
        </span>
        <span className="text-sm font-semibold tracking-wide">Asistente Virtual</span>
        {visible.length > 0 && (
          <Badge
            variant="secondary"
            className="ml-1 h-5 min-w-5 bg-white/20 px-1.5 text-xs text-white hover:bg-white/20"
          >
            {visible.length}
          </Badge>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader className="space-y-2">
            <SheetTitle className="flex items-center gap-2">
              <img
                src={portelinLogo}
                alt="Asistente Virtual"
                className="h-8 w-8 rounded-full object-cover"
              />
              <span>Asistente Virtual</span>
              <span className="text-sm font-normal text-muted-foreground">
                · tu asistente contable
              </span>
            </SheetTitle>
            <SheetDescription>
              Revisión automática de descuadres entre el total declarado y el
              calculado (base + IVA − IRPF).
            </SheetDescription>
          </SheetHeader>

          <div className="my-4 rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Hallazgos abiertos</span>
              <span className="font-semibold">{visible.length}</span>
            </div>
            {highCount > 0 && (
              <div className="mt-1 flex items-center gap-1.5 text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="text-xs">
                  {highCount} requieren atención antes de exportar
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto pr-1">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="mb-3 h-10 w-10 text-emerald-500" />
                <p className="font-medium">Todo en orden</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Asistente Virtual no ha detectado anomalías en las facturas actuales.
                </p>
              </div>
            ) : (
              visible.map((f) => {
                const sev = severityBadge[f.severity];
                return (
                  <div
                    key={f.id}
                    className={`rounded-lg border p-3 ${severityStyles[f.severity]}`}
                  >
                    <div className="mb-2 flex items-start gap-2">
                      <div className="mt-0.5 text-muted-foreground">{typeIcon(f.type)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-sm font-semibold">{f.title}</span>
                          <Badge variant={sev.variant} className="text-[10px]">
                            {sev.label}
                          </Badge>
                        </div>
                        <p className="truncate text-xs font-medium text-foreground/80">
                          {f.invoice}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setDismissed((prev) => new Set(prev).add(f.id))
                        }
                        className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                        aria-label="Descartar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-sm leading-snug text-foreground/90">{f.detail}</p>
                    <div className="mt-2 rounded-md bg-background/70 p-2 text-xs">
                      <span className="font-semibold text-primary">Sugerencia: </span>
                      {f.suggestion}
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => handleGoTo(f.fileId)}
                      >
                        Ir a la factura
                        <ChevronRight className="ml-0.5 h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 border-t pt-3 text-[11px] text-muted-foreground">
            Asistente Virtual revisa las facturas de tu sesión actual. Descarta los hallazgos que
            ya hayas validado.
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default AIValidatorDemo;